/**
 * Field key → the New Patient form markup for that field (KTD2). This map is
 * the source of truth for which catalog keys the form can render;
 * rendererCoverage.test.ts fails when an available catalog key has no entry
 * here. A Phase B unit adds its key here with its input.
 *
 * The markup is NewLeadPage's own field JSX, moved here unchanged apart from
 * taking its label, required mark, error and hint from the resolved settings
 * (via ConfiguredCard). Row names and grid classes reproduce the hard-coded
 * layout when fields are in catalog order.
 */
import type { ChangeEvent, RefObject } from 'react';
import { Camera, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { DialCodeSelect } from '@/components/ui/DialCodeSelect';
import { Checkbox } from '@/components/ui/Checkbox';
import { FormField } from '@/components/ui/FormField';
import { Textarea } from '@/components/ui/Textarea';
import { StateAutocomplete } from '@/components/ui/StateAutocomplete';
import { DatePicker } from '@/components/ui/DatePicker';
import { PhoneInput, PhoneLengthIndicator } from '@/components/medical/PhoneInput';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import type { CountryCodeOption } from '@/services/patients.service';
import type { PatientSummary } from '@/types/patient.types';
import type { SelectOption } from '@/types/common.types';
import { withHint, type FieldRendererMap } from './ConfiguredCard';
import { LAST_NAME_ERROR_KEY, LAST_NAME_LABEL, fieldAnchorId } from './errorList';

export const GENDER_OPTIONS: SelectOption<string>[] = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Other', label: 'Other' },
];

export const COUNTRY_OPTIONS: SelectOption<string>[] = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
];

export function computeAge(birthDate: string): number | null {
  if (!birthDate || birthDate.startsWith('0000')) return null;
  const dob = new Date(birthDate + 'T00:00:00');
  if (isNaN(dob.getTime())) return null;
  return Math.floor((Date.now() - dob.getTime()) / 31557600000);
}

type Setter<T> = (value: T) => void;

export type ScanPhase = 'idle' | 'scanning' | 'uploading' | 'extracting';

/** Everything the field markup reads from and writes to NewLeadPage. */
export interface NewLeadFieldContext {
  // Patient Information
  firstName: string;
  setFirstName: Setter<string>;
  middleName: string;
  setMiddleName: Setter<string>;
  lastName: string;
  setLastName: Setter<string>;
  maidenName: string;
  setMaidenName: Setter<string>;
  gender: string | null;
  setGender: Setter<string | null>;
  birthDate: string;
  setBirthDate: Setter<string>;
  // ID scan controls
  scan: {
    scannerAvailable: boolean;
    scanning: boolean;
    extractingUpload: boolean;
    scanPhase: ScanPhase;
    onScan: () => void;
    onTakePhoto: () => void;
    fileInputRef: RefObject<HTMLInputElement>;
    onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  };
  // Contact Information
  cellPhone: string;
  setCellPhone: Setter<string>;
  cellCountryCode: string;
  setCellCountryCode: Setter<string>;
  countryCodes: CountryCodeOption[];
  prefsLocked: boolean;
  smsOptIn: boolean;
  setSmsOptIn: Setter<boolean>;
  marketingSms: boolean;
  setMarketingSms: Setter<boolean>;
  email: string;
  setEmail: Setter<string>;
  emailOptIn: boolean;
  setEmailOptIn: Setter<boolean>;
  marketingEmails: boolean;
  setMarketingEmails: Setter<boolean>;
  address: string;
  setAddress: Setter<string>;
  city: string;
  setCity: Setter<string>;
  state: string;
  setState: Setter<string>;
  zip: string;
  setZip: Setter<string>;
  addressCountry: string;
  setAddressCountry: Setter<string>;
  // Plan Setup
  planTypeId: number | null;
  setPlanTypeId: Setter<number | null>;
  planTypeOptions: SelectOption<number>[];
  facilityId: number | null;
  setFacilityId: Setter<number | null>;
  facilityOptions: SelectOption<number>[];
  referralCategoryId: number | null;
  setReferralCategoryId: Setter<number | null>;
  referralCategoryOptions: SelectOption<number>[];
  referralSourceId: number | null;
  setReferralSourceId: Setter<number | null>;
  referralSourceOptions: SelectOption<number>[];
  isEstablishedPatientSource: boolean;
  referralPatient: PatientSummary | null;
  setReferralPatient: Setter<PatientSummary | null>;
  doctorId: number | null;
  setDoctorId: Setter<number | null>;
  doctorOptions: SelectOption<number>[];
  coordinatorId: number | null;
  setCoordinatorId: Setter<number | null>;
  coordinatorOptions: SelectOption<number>[];
  nurseId: number | null;
  setNurseId: Setter<number | null>;
  nurseOptions: SelectOption<number>[];
  // Interests
  revenueOpportunity: string;
  setRevenueOpportunity: Setter<string>;
  commitmentLevel: number | null;
  setCommitmentLevel: Setter<number | null>;
  commitmentOptions: SelectOption<number>[];
  timeframe: number | null;
  setTimeframe: Setter<number | null>;
  timeframeOptions: SelectOption<number>[];
  // Notes
  notes: string;
  setNotes: Setter<string>;
  /** Errors on inputs that are not catalog fields (Referring Patient). */
  errors: Record<string, string>;
}

const NAME_ROW = 'grid grid-cols-1 sm:grid-cols-2 gap-4';
const DEMOGRAPHICS_ROW = 'grid grid-cols-1 sm:grid-cols-3 gap-4';
const CONTACT_ROW = 'grid grid-cols-1 sm:grid-cols-2 gap-4';
const LOCALITY_ROW = 'grid grid-cols-1 sm:grid-cols-4 gap-4';
const PLAN_ROW = 'grid grid-cols-1 sm:grid-cols-2 gap-4';
const STAFF_ROW = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';
const INTERESTS_ROW = 'grid grid-cols-1 sm:grid-cols-3 gap-4';

export const FIELD_RENDERERS: FieldRendererMap<NewLeadFieldContext> = {
  // -------------------------------------------------------------------------
  // Patient Information
  // -------------------------------------------------------------------------
  first_name: {
    row: 'names',
    rowClassName: NAME_ROW,
    // With no custom label each name input has its own error (first_name,
    // last_name), as today. A custom label gives one error for the setting,
    // shown with its anchor on whichever input is empty (First Name when
    // both are).
    render: (p, c) => {
      const lastOnly = c.firstName.trim() !== '' && c.lastName.trim() === '' && !c.errors[LAST_NAME_ERROR_KEY];
      return (
        <FormField
          id={lastOnly ? undefined : p.anchorId}
          label="First Name"
          required={p.required}
          error={lastOnly ? undefined : p.error}
        >
          {withHint(
            <Input
              value={c.firstName}
              onChange={(event) => c.setFirstName(event.target.value)}
              placeholder="First name"
            />,
            p,
          )}
        </FormField>
      );
    },
    companion: {
      after: 'middle_name',
      render: (p, c) => {
        const ownError = c.errors[LAST_NAME_ERROR_KEY];
        const lastOnly = c.firstName.trim() !== '' && c.lastName.trim() === '' && !ownError;
        return (
          <FormField
            id={lastOnly ? p.anchorId : fieldAnchorId(LAST_NAME_ERROR_KEY)}
            label={LAST_NAME_LABEL}
            required={p.required}
            error={ownError || (c.lastName.trim() === '' ? p.error : undefined)}
          >
            <Input
              value={c.lastName}
              onChange={(event) => c.setLastName(event.target.value)}
              placeholder="Last name"
            />
          </FormField>
        );
      },
    },
  },
  middle_name: {
    row: 'names',
    rowClassName: NAME_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Input
            value={c.middleName}
            onChange={(event) => c.setMiddleName(event.target.value)}
            placeholder="Middle name"
          />,
          p,
        )}
      </FormField>
    ),
  },
  maiden_name: {
    row: 'names',
    rowClassName: NAME_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Input
            value={c.maidenName}
            onChange={(event) => c.setMaidenName(event.target.value)}
            placeholder="Maiden name"
          />,
          p,
        )}
      </FormField>
    ),
  },
  gender: {
    row: 'demographics',
    rowClassName: DEMOGRAPHICS_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select options={GENDER_OPTIONS} value={c.gender} onChange={c.setGender} placeholder="Select gender" />,
          p,
        )}
      </FormField>
    ),
  },
  birth_date: {
    row: 'demographics',
    rowClassName: DEMOGRAPHICS_ROW,
    render: (p, c) => {
      const age = computeAge(c.birthDate);
      return (
        <>
          <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error} htmlFor="new-lead-birth-date">
            {withHint(
              <DatePicker id="new-lead-birth-date" birthDate value={c.birthDate} onChange={c.setBirthDate} />,
              p,
            )}
          </FormField>
          {/* `max-sm:empty:hidden` because this row's grid collapses to one column
              at `sm`, and with no birth date entered this cell renders no child node
              at all — so below that width it was reserving a blank row of pt-5 plus
              the grid gap under the field. Above `sm` it still occupies the third
              track, empty or not, exactly as before. */}
          <span className="self-center pt-5 text-sm font-medium text-slate-600 max-sm:empty:hidden">
            {age != null ? `Age: ${age}` : ''}
          </span>
        </>
      );
    },
  },
  id_scan: {
    row: 'id_scan',
    slot: 'header',
    render: (p, c) => (
      <div
        id={p.anchorId}
        role="group"
        aria-label={p.label}
        aria-describedby={p.hintId}
        className="flex items-center gap-2 flex-wrap"
      >
        {c.scan.scannerAvailable && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={c.scan.onScan}
            loading={c.scan.scanning}
            disabled={c.scan.scanning || c.scan.extractingUpload}
          >
            {c.scan.scanPhase === 'scanning'
              ? 'Scanning...'
              : c.scan.scanPhase === 'uploading'
                ? 'Uploading...'
                : c.scan.scanPhase === 'extracting' && c.scan.scanning
                  ? 'Extracting...'
                  : 'Scan ID'}
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Upload className="h-4 w-4" />}
          onClick={() => c.scan.fileInputRef.current?.click()}
          disabled={c.scan.scanning || c.scan.extractingUpload}
        >
          Upload
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Camera className="h-4 w-4" />}
          onClick={c.scan.onTakePhoto}
          disabled={c.scan.scanning || c.scan.extractingUpload}
        >
          Take Photo
        </Button>
        <input
          ref={c.scan.fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={c.scan.onFileChange}
        />
      </div>
    ),
  },

  // -------------------------------------------------------------------------
  // Contact Information
  // -------------------------------------------------------------------------
  cell_phone: {
    row: 'contact',
    rowClassName: CONTACT_ROW,
    render: (p, c) => (
      <div className="space-y-2">
        <FormField
          id={p.anchorId}
          label={p.label}
          required={p.required}
          error={p.error}
          labelExtra={<PhoneLengthIndicator value={c.cellPhone} />}
        >
          {withHint(
            <div className="flex gap-2">
              <div className="w-24 flex-shrink-0">
                <DialCodeSelect value={c.cellCountryCode} onChange={c.setCellCountryCode} countries={c.countryCodes} />
              </div>
              <div className="flex-1">
                <PhoneInput
                  value={c.cellPhone}
                  countryCode={c.cellCountryCode}
                  onChange={(digits) => c.setCellPhone(digits)}
                />
              </div>
            </div>,
            p,
          )}
        </FormField>
        <div className="flex items-center gap-4">
          <Checkbox
            label="Transactional SMS"
            disabled={c.prefsLocked}
            checked={c.smsOptIn}
            onChange={(event) => c.setSmsOptIn(event.target.checked)}
          />
          <Checkbox
            label="Marketing SMS"
            disabled={c.prefsLocked}
            checked={c.marketingSms}
            onChange={(event) => c.setMarketingSms(event.target.checked)}
          />
        </div>
      </div>
    ),
  },
  email: {
    row: 'contact',
    rowClassName: CONTACT_ROW,
    render: (p, c) => (
      <div className="space-y-2">
        <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
          {withHint(
            <Input
              type="email"
              value={c.email}
              onChange={(event) => c.setEmail(event.target.value)}
              placeholder="email@example.com"
            />,
            p,
          )}
        </FormField>
        <div className="flex items-center gap-4">
          <Checkbox
            label="Transactional Email"
            disabled={c.prefsLocked}
            checked={c.emailOptIn}
            onChange={(event) => c.setEmailOptIn(event.target.checked)}
          />
          <Checkbox
            label="Marketing Email"
            disabled={c.prefsLocked}
            checked={c.marketingEmails}
            onChange={(event) => c.setMarketingEmails(event.target.checked)}
          />
        </div>
      </div>
    ),
  },
  address: {
    row: 'address',
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Input value={c.address} onChange={(event) => c.setAddress(event.target.value)} placeholder="Street address" />,
          p,
        )}
      </FormField>
    ),
  },
  city: {
    row: 'locality',
    rowClassName: LOCALITY_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(<Input value={c.city} onChange={(event) => c.setCity(event.target.value)} placeholder="City" />, p)}
      </FormField>
    ),
  },
  state: {
    row: 'locality',
    rowClassName: LOCALITY_ROW,
    // Label: "Province" for Canada via the caller's captions.
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(<StateAutocomplete value={c.state} onChange={c.setState} country={c.addressCountry} />, p)}
      </FormField>
    ),
  },
  zip: {
    row: 'locality',
    rowClassName: LOCALITY_ROW,
    // Label: "Postal Code" for Canada via the caller's captions.
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Input
            value={c.zip}
            onChange={(event) => c.setZip(event.target.value)}
            placeholder={c.addressCountry === 'CA' ? 'Postal code' : 'ZIP code'}
            maxLength={c.addressCountry === 'CA' ? 7 : 9}
          />,
          p,
        )}
      </FormField>
    ),
  },
  country: {
    row: 'locality',
    rowClassName: LOCALITY_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select
            options={COUNTRY_OPTIONS}
            value={c.addressCountry}
            onChange={(v) => {
              c.setAddressCountry(v ?? 'US');
              c.setState('');
            }}
            placeholder="Country"
          />,
          p,
        )}
      </FormField>
    ),
  },

  // -------------------------------------------------------------------------
  // Plan Setup
  // -------------------------------------------------------------------------
  plan_type: {
    row: 'plan',
    rowClassName: PLAN_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select
            options={c.planTypeOptions}
            value={c.planTypeId}
            onChange={c.setPlanTypeId}
            placeholder="Select plan type"
            searchable
          />,
          p,
        )}
      </FormField>
    ),
  },
  facility: {
    row: 'plan',
    rowClassName: PLAN_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select
            options={c.facilityOptions}
            value={c.facilityId}
            onChange={c.setFacilityId}
            placeholder="Select facility"
            searchable
          />,
          p,
        )}
      </FormField>
    ),
  },
  referral_select_1_anchor: {
    row: 'referral',
    rowClassName: PLAN_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select
            options={c.referralCategoryOptions}
            value={c.referralCategoryId ?? 0}
            onChange={(val) => {
              c.setReferralCategoryId(val === 0 ? null : val);
              c.setReferralSourceId(null);
            }}
            placeholder="Select category"
            searchable
          />,
          p,
        )}
      </FormField>
    ),
    // The referring patient belongs to the category (an established-patient
    // referral), so it follows the category's row.
    extra: (_p, c) =>
      c.isEstablishedPatientSource ? (
        <FormField id="field-referralPatient" label="Referring Patient" required error={c.errors.referralPatient}>
          <PatientSearchInput
            selectedPatient={c.referralPatient}
            onSelect={c.setReferralPatient}
            placeholder="Search for referring patient…"
          />
        </FormField>
      ) : null,
  },
  referral_name: {
    row: 'referral',
    rowClassName: PLAN_ROW,
    render: (p, c) =>
      c.referralCategoryId != null && c.referralSourceOptions.length > 1 ? (
        <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
          {withHint(
            <Select
              options={c.referralSourceOptions}
              value={c.referralSourceId ?? 0}
              onChange={(val) => c.setReferralSourceId(val === 0 ? null : val)}
              placeholder="Select source"
              searchable
            />,
            p,
          )}
        </FormField>
      ) : null,
  },
  doctor: {
    row: 'staff',
    rowClassName: STAFF_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select
            options={c.doctorOptions}
            value={c.doctorId}
            onChange={c.setDoctorId}
            placeholder={`Select ${p.label.toLowerCase()}`}
            searchable
          />,
          p,
        )}
      </FormField>
    ),
  },
  coordinator: {
    row: 'staff',
    rowClassName: STAFF_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select
            options={c.coordinatorOptions}
            value={c.coordinatorId}
            onChange={c.setCoordinatorId}
            placeholder={`Select ${p.label.toLowerCase()}`}
            searchable
          />,
          p,
        )}
      </FormField>
    ),
  },
  nurse: {
    row: 'staff',
    rowClassName: STAFF_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select
            options={c.nurseOptions}
            value={c.nurseId}
            onChange={c.setNurseId}
            placeholder={`Select ${p.label.toLowerCase()}`}
            searchable
          />,
          p,
        )}
      </FormField>
    ),
  },

  // -------------------------------------------------------------------------
  // Interests
  // -------------------------------------------------------------------------
  ball_park_given: {
    row: 'interests',
    rowClassName: INTERESTS_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Input
            type="number"
            step="0.01"
            min="0"
            value={c.revenueOpportunity}
            onChange={(event) => c.setRevenueOpportunity(event.target.value)}
            placeholder="0.00"
          />,
          p,
        )}
      </FormField>
    ),
  },
  ball_park: {
    row: 'interests',
    rowClassName: INTERESTS_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select
            options={c.commitmentOptions}
            value={c.commitmentLevel}
            onChange={c.setCommitmentLevel}
            placeholder="Select One..."
          />,
          p,
        )}
      </FormField>
    ),
  },
  timeframe: {
    row: 'interests',
    rowClassName: INTERESTS_ROW,
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Select options={c.timeframeOptions} value={c.timeframe} onChange={c.setTimeframe} placeholder="Select One..." />,
          p,
        )}
      </FormField>
    ),
  },

  // -------------------------------------------------------------------------
  // Notes
  // -------------------------------------------------------------------------
  notes: {
    row: 'notes',
    render: (p, c) => (
      <FormField id={p.anchorId} label={p.label} required={p.required} error={p.error}>
        {withHint(
          <Textarea
            value={c.notes}
            onChange={(event) => c.setNotes(event.target.value)}
            placeholder="Enter intake notes that should follow the new plan..."
            rows={5}
          />,
          p,
        )}
      </FormField>
    ),
  },
};
